import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Box, Grid, Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Loader } from 'ui';
import { useQuery } from 'shared/hooks';

import { shopViews } from '../shared/constants/shopViews';
import {
  fetchShopProductsAction,
  fetchShopHistoryAction,
  setShopActiveViewAction,
  fetchShopProductPropertiesAction,
} from '../store/shopActions';
import { ShopFavorites } from '../components/ShopFavorites';
import { getShopProducts } from '../selectors/shopSelectors';
import { ShopProduct } from '../components/ShopProduct/ShopProduct';
import { ShopSortSelect } from '../components/ShopSortSelect/ShopSortSelect';
import { ShopFilters } from '../components/ShopFilters/ShopFilters';
import { ShopBasket } from '../components/ShopBasket';
import { ShopPurchases } from '../components/ShopPurchases';

const useStyle = makeStyles(() => ({
  title: {
    display: 'flex',
    alignItems: 'center',
  },
  counter: {
    fontSize: 15,
    marginRight: 20,
    marginBottom: 2,
    fontWeight: 500,
  },
  button: {
    height: 45,
    marginLeft: 10,
  },
  noDataText: {
    fontSize: 18,
  },
}));

const Shop = () => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const { tab } = useQuery();
  const navigate = useNavigate();
  const activeBasketState = useState(0);
  const urlsState = useState({});

  const { products: allProducts, activeView } = useSelector(({ shop }) => shop);
  const products = useSelector(getShopProducts);
  const activeProducts = allProducts && allProducts.data.filter(({ active }) => active);

  useEffect(() => {
    dispatch(fetchShopProductPropertiesAction());
    dispatch(fetchShopProductsAction());
    dispatch(fetchShopHistoryAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const [, setActiveBasket] = activeBasketState;

    dispatch(setShopActiveViewAction({ view: tab || null }));
    setActiveBasket(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleViewChange = (view) => {
    navigate({
      search: tab === view ? '' : `?tab=${view}`,
    });
  };

  if (products.isPending) {
    return <Loader center />;
  }

  return (
    <>
      <Box my={2}>
        <ShopFilters showSelectFilters={false}>
          {Object.keys(shopViews).map((view) => (
            <Button
              key={view}
              variant={activeView === view ? 'contained' : 'outlined'}
              color="primary"
              className={classes.button}
              onClick={() => handleViewChange(view)}
            >
              {t(`My ${shopViews[view]}`)}
            </Button>
          ))}
        </ShopFilters>
      </Box>
      {!activeView && (
        <>
          <Box mb={2}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.title}>
                <Typography variant="h5">{t('Our offerings')}</Typography>
              </Grid>
              <Grid item>
                <Box display="flex" alignItems="center">
                  <Typography component="span" className={classes.counter}>
                    {products.data.length} of {activeProducts.length}
                  </Typography>
                  <ShopSortSelect />
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Paper elevation={3}>
            {products.data.length ? (
              <Box padding={6}>
                {products.data.map((item) => (
                  <ShopProduct data={item} key={item.id} />
                ))}
              </Box>
            ) : (
              <Box padding={3}>
                <Typography variant="body1" className={classes.noDataText}>
                  {t('No products found')}
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
      {activeView === shopViews.purchases && <ShopPurchases />}
      {activeView === shopViews.favorites && <ShopFavorites />}
      {activeView === shopViews.basket && (
        <ShopBasket activeState={activeBasketState} urlsState={urlsState} />
      )}
    </>
  );
};

export { Shop };
